import { ChangeDetectorRef, Component, DestroyRef, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacyOptionModule as MatOptionModule } from '@angular/material/legacy-core';

import { forkJoin, map } from 'rxjs';

import { RelatedToType } from '@shared/enums';
import { AddressPart } from '@shared/models';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { ProjectTemplate, Property } from 'app/modules/projects/projects.types';
import { AccountService } from 'app/core/services/account.service';
import { PropertyType } from 'app/core/models/account.types';
import { PropertiesService } from 'app/modules/properties/properties.service';
import * as CustomValidator from '@shared/directives/custom-validators.directive';
import { DirectivesModule } from '@shared/directives/directives.module';
import { TsButtonModule } from 'tsui';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';

declare interface DialogDataInterface {
  byPass?: boolean;
  model: any;
  multi_select?: boolean;
  related_to_type: RelatedToType;
  searchTerm?: string;
}

@Component({
  selector: 'add-property-modal',
  standalone: true,
  imports: [
    TsDialogComponent,
    TsButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    FormsModule,
    ReactiveFormsModule,
    DirectivesModule,
  ],
  templateUrl: './add-property-modal.component.html',
  styleUrls: ['./add-property-modal.component.scss'],
})
export class AddPropertyModalComponent implements OnInit {
  propertyForm: FormGroup;
  propertyTypes: PropertyType[] = [];
  propertyTemplates: ProjectTemplate<any>[] = [];
  isLoading = false;
  isOptionsLoading = true;

  get isSaveBtnDisabled(): boolean {
    return this.isLoading || this.propertyForm.invalid || this.propertyForm.pristine;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly data: DialogDataInterface,
    private readonly matDialogRef: MatDialogRef<AddPropertyModalComponent>,
    private readonly formBuilder: FormBuilder,
    private readonly projectsService: ProjectsService,
    private readonly propertiesService: PropertiesService,
    private readonly accountService: AccountService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.loadOptions();
  }

  private buildForm(): void {
    this.propertyForm = this.formBuilder.group({
      name: [this.data?.searchTerm || '', [Validators.required, CustomValidator.noWhiteSpaceValidator]],
      property_type_id: [null, [Validators.required]],
      template_id: [null],
      address: this.formBuilder.group({
        address_string: ['', [Validators.required, CustomValidator.noWhiteSpaceValidator]],
        street: [''],
        city: [''],
        state: [''],
        postal_code: [''],
        latitude: [''],
        longitude: [''],
      }),
    });
  }

  private loadOptions(): void {
    this.isOptionsLoading = true;
    forkJoin({
      standardDropdowns: this.accountService.getStandardDropdowns(),
      projectTemplates: this.projectsService.getProjectTemplates(),
    })
      .pipe(
        map(({ standardDropdowns, projectTemplates }) => {
          return {
            propertyTypes: (standardDropdowns?.property_types || []).filter((option) => !option.hidden),
            propertyTemplates: (projectTemplates || [])
              .filter((option) => !option.hidden && option.related_to_type === RelatedToType.Property)
              .sort((a, b) => a.rank - b.rank),
          };
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ propertyTypes, propertyTemplates }) => {
        this.propertyTypes = propertyTypes;
        this.propertyTemplates = propertyTemplates;
        this.isOptionsLoading = false;
        this.changeDetectorRef.markForCheck();
      });
  }

  setAddress(addressString: string): void {
    this.propertyForm.get('address').get('address_string').setValue(addressString);
  }

  onSelectAddressParts({
    locality,
    administrative_area_level_1,
    street_number,
    route,
    postal_code,
    lat,
    lng,
  }: AddressPart): void {
    this.propertyForm.get('address').get('city').setValue(locality);
    this.propertyForm.get('address').get('state').setValue(administrative_area_level_1);
    this.propertyForm.get('address').get('postal_code').setValue(postal_code);
    this.propertyForm.get('address').get('latitude').setValue(lat);
    this.propertyForm.get('address').get('longitude').setValue(lng);

    if (route) {
      const streetValue = street_number ? `${street_number} ${route}` : route;
      this.propertyForm.get('address').get('street').setValue(streetValue);
    } else {
      this.propertyForm.get('address').get('street').setValue('');
    }
  }

  saveProperty(): void {
    this.propertyForm.disable();
    this.isLoading = true;

    const property = this.propertyForm.value;
    this.propertiesService
      .saveProperty(property)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (createdProperty: Property) => {
          this.matDialogRef.close({
            model: { ...createdProperty, name: createdProperty?.name || `Property ${createdProperty?.id}` },
            multi_select: this.data?.multi_select,
          });
        },
        error: () => {
          this.isLoading = false;
          this.propertyForm.enable();
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  discard(): void {
    this.matDialogRef.close();
  }
}
